/**
 * Design Tokens: Colors
 * Color system from Figma Design System: Crop Circle System
 *
 * All color values are defined in the Extended section
 * File ID: knAFKgkS8wkIQ47XbZAe4S
 */

export const colors = {
  /**
   * Brand Colors
   * Core brand identity colors
   */
  brand: {
    core: '#FE5000', // brand-core - Pantone Orange 021C
    accent: '#FF9C47', // brand-accent - Softer, warmer marketing orange
    white: '#FCFCFC', // brand-white - Brand white
    neutral: '#F3F3F3', // brand-neutral - Brand neutral
    layer: '#7A8394', // brand-layer - Layer/overlay color
    surface: '#3A3F4D', // brand-surface - Surface color
    black: '#020202', // brand-black - Brand black
    fullWhite: '#FFFFFF', // brand-white - Brand white
  },

  /**
   * Neutral - Grayscale
   * Used for backgrounds, borders, and text
   * Full 8-step scale from light to dark
   */
  neutral: {
    100: '#FCFCFC', // neutral-100 - Lightest gray
    200: '#F3F3F3', // neutral-200 - Very light gray
    300: '#D8D8D8', // neutral-300 - Light gray
    400: '#B4B4B4', // neutral-400 - Medium-light gray
    500: '#6C6C6C', // neutral-500 - Medium gray
    600: '#2F2F2F', // neutral-600 - Medium-dark gray
    700: '#1A1A1A', // neutral-700 - Dark gray
    800: '#020202', // neutral-800 - Very dark gray
  },

  /**
   * Slate - Cool Grayscale
   * Alternative grayscale with blue undertones
   * Full 8-step scale from light to dark
   */
  slate: {
    100: '#ECEEF1', // slate-100 - Lightest slate
    200: '#C8CDD5', // slate-200 - Very light slate
    300: '#A1A8B5', // slate-300 - Light slate
    400: '#7A8394', // slate-400 - Medium-light slate
    500: '#4F5667', // slate-500 - Medium slate
    600: '#3A3F4D', // slate-600 - Medium-dark slate
    700: '#262A33', // slate-700 - Dark slate
    800: '#1A1D24', // slate-800 - Very dark slate
  },

  /**
   * Orange - Brand Color Scale
   * Complete 8-step orange scale
   * orange-400 = brand.core
   */
  orange: {
    100: '#FFF0E5', // orange-100 - Lightest orange
    200: '#FFD7B0', // orange-200 - Very light orange
    250: '#FFB368', // orange-250 - CardButton default orange
    300: '#FF8433', // orange-300 - Light orange (CardButton hover)
    400: '#FE5000', // orange-400 - Brand core ⭐ (CardButton active)
    500: '#C84B0D', // orange-500 - Medium orange
    600: '#8C2F07', // orange-600 - Medium-dark orange
    700: '#441300', // orange-700 - Dark orange
    800: '#2A0C00', // orange-800 - Very dark orange
  },

  /**
   * Semantic Colors
   * Convey meaning and status with light and dark variants
   */
  semantic: {
    // Info colors (blue)
    infoLight: '#D0E5F9', // info-light - Light info background
    info: '#4A90E2', // info - Primary info blue
    infoDark: '#1F5B99', // info-dark - Dark info text/icon

    // Success colors (green)
    successLight: '#D3F4E7', // success-light - Light success background
    success: '#36B37E', // success - Primary success green
    successDark: '#24835D', // success-dark - Dark success text/icon

    // Warning colors (yellow/amber)
    warningLight: '#FFF4C1', // warning-light - Light warning background
    warning: '#F9C431', // warning - Primary warning yellow
    warningDark: '#D7A321', // warning-dark - Dark warning text/icon

    // Error colors (red)
    errorLight: '#FFD9D2', // error-light - Light error background
    error: '#E24D2A', // error - Primary error red
    errorDark: '#B32A12', // error-dark - Dark error text/icon
  },

  /**
   * System Colors
   * Utility colors for common UI patterns
   */
  system: {
    focusRing: '#4A90E2', // focus-ring - Focus indicator
    link: '#4A90E2', // link - Hyperlink color
    disabled: '#CCCCCC', // disabled - Disabled state
    muted: '#9FA6B2', // muted - Muted text
    textPrimary: '#2F2F2F', // text-primary - Default text
    textSecondary: '#6C6C6C', // text-secondary - Secondary text
    textPlaceholder: '#B4B4B4', // text-placeholder - Placeholder text
    textInverted: '#FFFFFF', // text-inverted - White text
    borderDefault: '#D8D8D8', // border-default - Default border
  },

  /**
   * Label Colors
   * Used for tags, badges, and categorization
   * 6 distinct colors for visual variety
   */
  label: {
    sky: '#2CA9E0', // label-sky - Cyan, fresh but distinct from info
    magenta: '#D649A6', // label-magenta - Vibrant magenta
    violet: '#7E4CC9', // label-violet - Deep violet
    indigo: '#635BFF', // label-indigo - Techy indigo
    sand: '#C7A46A', // label-sand - Earthy neutral
    rose: '#E06C75', // label-rose - Warm rose, avoids semantic red
  },
} as const;

/**
 * Color types for TypeScript
 */
export type ColorToken = typeof colors;
export type ColorPath = keyof typeof colors;
